'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TYPING_MAX_ATTEMPTS,
  TYPING_MIN_ACCURACY,
  TYPING_MIN_WPM,
  TYPING_STORIES,
  TYPING_TEST_SECONDS,
  computeTypingResult,
  typingPassed,
  type TypingStory,
} from '@/lib/recruitment/typing-test';

export type TypingSavedResult = { wpm: number; accuracy: number; attempts: number };

type Phase = 'intro' | 'running' | 'saving' | 'done';

function splitWords(text: string): string[] {
  return text.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
}

/**
 * 1-minute typing test (typingtest.com style). The timer arms on the first
 * keystroke and the transcript is only evaluated when time is up, so the
 * candidate can go back and correct mistakes for the whole minute. Passing
 * bar: 25 WPM at 90% accuracy - below it ONE retake is offered (2 attempts
 * max, enforced server-side).
 */
export function TypingStep({
  token,
  initialResult,
  initialStoryKey,
  onCompleted,
  onExpired,
  onBack,
  onContinue,
}: {
  token: string;
  /** Stored result from a previous session, else null. */
  initialResult: TypingSavedResult | null;
  /** Story used on the stored attempt - avoided when picking the retake story. */
  initialStoryKey: string | null;
  onCompleted: (completedSteps: string[]) => void;
  onExpired: () => void;
  onBack: (() => void) | null;
  onContinue: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(initialResult ? 'done' : 'intro');
  const [story, setStory] = useState<TypingStory | null>(null);
  const [typed, setTyped] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(TYPING_TEST_SECONDS);
  const [result, setResult] = useState<TypingSavedResult | null>(initialResult);
  const [error, setError] = useState('');
  // The countdown arms on the FIRST keystroke, not on Start.
  const [startedAt, setStartedAt] = useState<number | null>(null);

  const startedAtRef = useRef<number>(0);
  const typedRef = useRef('');
  typedRef.current = typed;
  const storyRef = useRef<TypingStory | null>(null);
  storyRef.current = story;
  const lastStoryKeyRef = useRef<string | null>(initialStoryKey);
  const finishedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const currentWordRef = useRef<HTMLSpanElement | null>(null);

  const storyWords = useMemo(() => (story ? splitWords(story.text) : []), [story]);
  const typedWords = useMemo(() => splitWords(typed), [typed]);
  // While the trailing word is still being typed it is "current", not judged.
  const endsWithSpace = /\s$/.test(typed);
  const judgedCount = endsWithSpace ? typedWords.length : Math.max(0, typedWords.length - 1);
  const currentIndex = judgedCount;

  useEffect(() => {
    currentWordRef.current?.scrollIntoView({ block: 'nearest' });
  }, [currentIndex]);

  const finish = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const activeStory = storyRef.current;
    if (!activeStory) return;
    const elapsedSeconds = Math.min(
      Math.max(Math.round((Date.now() - startedAtRef.current) / 1000), 1),
      TYPING_TEST_SECONDS
    );
    setPhase('saving');
    setError('');
    try {
      const res = await fetch(`/api/public/intl-recruitment/${token}/typing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storyKey: activeStory.key,
          typedText: typedRef.current,
          elapsedSeconds,
        }),
      });
      if (res.status === 410) {
        onExpired();
        return;
      }
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Let the candidate retry the SAVE (not the test) - keep the transcript.
        setError(d.error || 'Could not save your result. Please try again.');
        setPhase('running');
        setSecondsLeft(0);
        return;
      }
      setResult({
        wpm: typeof d.wpm === 'number' ? d.wpm : 0,
        accuracy: typeof d.accuracy === 'number' ? d.accuracy : 0,
        attempts: typeof d.attempts === 'number' ? d.attempts : 1,
      });
      if (Array.isArray(d.completedSteps)) onCompleted(d.completedSteps);
      setPhase('done');
    } catch {
      setError('Could not save your result. Check your connection and try again.');
      setPhase('running');
      setSecondsLeft(0);
    }
  }, [token, onCompleted, onExpired]);

  // Countdown - driven by wall clock so tab throttling can't stretch the minute.
  // Only runs once the first character has been typed; the transcript stays
  // editable (corrections allowed) until it hits zero.
  useEffect(() => {
    if (phase !== 'running' || startedAt === null) return;
    const timer = window.setInterval(() => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      const left = Math.max(0, Math.ceil(TYPING_TEST_SECONDS - elapsed));
      setSecondsLeft(left);
      if (left <= 0) void finish();
    }, 200);
    return () => window.clearInterval(timer);
  }, [phase, startedAt, finish]);

  const start = () => {
    // Retakes get a different story than the previous attempt.
    const pool = TYPING_STORIES.filter((s) => s.key !== lastStoryKeyRef.current);
    const pick = pool[Math.floor(Math.random() * pool.length)] ?? TYPING_STORIES[0];
    lastStoryKeyRef.current = pick.key;
    setStory(pick);
    setTyped('');
    setError('');
    finishedRef.current = false;
    startedAtRef.current = 0;
    setStartedAt(null);
    setSecondsLeft(TYPING_TEST_SECONDS);
    setPhase('running');
    window.setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const retrySave = () => {
    finishedRef.current = false;
    void finish();
  };

  const livePreview =
    phase === 'running' && story && startedAt !== null
      ? computeTypingResult(story.text, typed, Math.max(1, TYPING_TEST_SECONDS - secondsLeft))
      : null;

  const minimumLabel = `${TYPING_MIN_WPM} WPM at ${TYPING_MIN_ACCURACY}% accuracy`;

  // ── Done - result (locked, or one retake open) ────────────────────────────
  if (phase === 'done' && result) {
    const passed = typingPassed(result.wpm, result.accuracy);
    const canRetry = !passed && result.attempts < TYPING_MAX_ATTEMPTS;
    return (
      <div className="space-y-5">
        <div
          className={`bg-gray-900 border rounded-2xl p-6 text-center ${
            passed ? 'border-emerald-800' : 'border-amber-800'
          }`}
        >
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">
            {passed
              ? 'Typing test completed'
              : canRetry
                ? 'Below the minimum score'
                : 'Typing test completed - second attempt recorded'}
          </p>
          <div className="flex items-end justify-center gap-10">
            <div>
              <p
                className={`text-5xl font-bold tabular-nums ${
                  result.wpm >= TYPING_MIN_WPM ? 'text-emerald-400' : 'text-amber-400'
                }`}
              >
                {result.wpm}
              </p>
              <p className="text-xs text-gray-400 mt-1">Words per minute</p>
            </div>
            <div>
              <p
                className={`text-5xl font-bold tabular-nums ${
                  result.accuracy >= TYPING_MIN_ACCURACY ? 'text-white' : 'text-amber-400'
                }`}
              >
                {result.accuracy}%
              </p>
              <p className="text-xs text-gray-400 mt-1">Accuracy</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            {passed ? (
              <>Your result meets the minimum of {minimumLabel} and has been saved with your application.</>
            ) : canRetry ? (
              <>
                The minimum is {minimumLabel}. This was attempt {result.attempts} of{' '}
                {TYPING_MAX_ATTEMPTS} - you have one more try, with a different story.
              </>
            ) : (
              <>
                The minimum is {minimumLabel}. Both attempts are recorded with your
                application - you can still continue and submit.
              </>
            )}
          </p>
        </div>
        <div className="flex gap-3 pt-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-5 py-3.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm font-medium transition-colors"
            >
              Back
            </button>
          )}
          {canRetry ? (
            <button
              type="button"
              onClick={start}
              className="btn btn-primary flex-1 py-3.5 font-semibold text-sm h-auto"
            >
              Try Again - Attempt {result.attempts + 1} of {TYPING_MAX_ATTEMPTS}
            </button>
          ) : (
            <button
              type="button"
              onClick={onContinue}
              className="btn btn-primary flex-1 py-3.5 font-semibold text-sm h-auto"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Intro ─────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="space-y-5">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-3">
          <p className="text-sm font-semibold text-white">1-Minute Typing Test</p>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex gap-2"><span className="text-emerald-400">•</span> You will see a short story. Type it exactly as shown, as fast and as accurately as you can.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> The timer starts the moment you type your first character and runs for {TYPING_TEST_SECONDS} seconds.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> You can go back and correct mistakes at any time - your result is only evaluated when the time is up.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> Copy and paste is disabled - every word must be typed.</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> The minimum score is <strong className="text-white">{minimumLabel}</strong>. If you score below it you get one more attempt ({TYPING_MAX_ATTEMPTS} total).</li>
            <li className="flex gap-2"><span className="text-emerald-400">•</span> Your words-per-minute (WPM) and accuracy are saved with your application.</li>
          </ul>
        </div>
        <div className="flex gap-3 pt-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-5 py-3.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm font-medium transition-colors"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={start}
            className="btn btn-primary flex-1 py-3.5 font-semibold text-sm h-auto"
          >
            Start Test
          </button>
        </div>
      </div>
    );
  }

  // ── Running / saving ──────────────────────────────────────────────────────
  const saveFailed = phase === 'running' && startedAt !== null && secondsLeft <= 0;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p
          className={`text-3xl font-bold tabular-nums ${
            startedAt !== null && secondsLeft <= 10 ? 'text-red-400' : 'text-white'
          }`}
        >
          {Math.floor(Math.max(0, secondsLeft) / 60)}:
          {String(Math.max(0, secondsLeft) % 60).padStart(2, '0')}
        </p>
        {livePreview ? (
          <p className="text-xs text-gray-400 tabular-nums">
            {livePreview.wpm} WPM · {livePreview.accuracy}% accuracy
          </p>
        ) : (
          <p className="text-xs text-gray-500">The timer starts when you type your first character.</p>
        )}
      </div>

      {/* Story with live per-word feedback; selection disabled to keep copying out. */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 max-h-56 overflow-y-auto select-none leading-relaxed text-[15px]">
        {storyWords.map((word, i) => {
          const isCurrent = i === currentIndex;
          const judged = i < judgedCount;
          const correct = judged && typedWords[i] === word;
          return (
            <span
              key={i}
              ref={isCurrent ? currentWordRef : undefined}
              className={
                isCurrent
                  ? 'bg-emerald-600/30 text-white rounded px-0.5 underline underline-offset-4'
                  : judged
                    ? correct
                      ? 'text-emerald-400'
                      : 'text-red-400 line-through'
                    : 'text-gray-400'
              }
            >
              {word}{' '}
            </span>
          );
        })}
      </div>

      <textarea
        ref={textareaRef}
        value={typed}
        onChange={(e) => {
          if (phase !== 'running' || finishedRef.current) return;
          if (startedAtRef.current === 0 && e.target.value.length > 0) {
            startedAtRef.current = Date.now();
            setStartedAt(startedAtRef.current);
          }
          setTyped(e.target.value);
        }}
        onPaste={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
        disabled={phase !== 'running' || saveFailed}
        rows={4}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        placeholder="Start typing the story here…"
        className="w-full rounded-xl bg-gray-900 border border-gray-700 focus:border-emerald-600 focus:outline-none text-white text-sm p-3.5 placeholder-gray-600 disabled:opacity-60"
      />

      {phase === 'saving' && (
        <p className="text-sm text-gray-400 flex items-center gap-2">
          <span className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
          Time&apos;s up - saving your result…
        </p>
      )}
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-4 py-3 text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={retrySave}
            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-red-700 hover:bg-red-600 text-white font-medium transition-colors"
          >
            Retry save
          </button>
        </div>
      )}
    </div>
  );
}
